import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { ReportSummaryResponse } from '@/modules/Reports/report.types';

import { useBaseReportSummaryQuery } from '../../queries';
import { ReplenishmentInvoiceSummaryReportParams } from '../ReplenishmentInvoiceReport.types';

export const QUERY_KEY = 'GET_REPLENISHMENT_INVOICE_SUMMARY';
interface ReplenishmentInvoiceResponse extends ReportSummaryResponse {
  date_from: string;
  date_to: string;
}
const useReplenishmentInvoiceReportSummaryQuery = (params: ReplenishmentInvoiceSummaryReportParams) => {
  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);

  const {
    data,
    rawData,
    isLoading,
    isLoadingMore,
    isSuccess,
    isEmptyData,
    lastTimeUpdateDate,
    planOptions,
    isHasNextPage,
    handleLoadMore,
    resetPagination,
  } = useBaseReportSummaryQuery<ReplenishmentInvoiceResponse>(
    API_URL.REPORTS_REPLENISHMENT_INVOICE_REPORT_SUMMARY,
    [QUERY_KEY, params.employerId, params?.invoiceID, params.startDate, params.endDate],
    {
      employer_id: params.employerId,
      invoice_id: params.invoiceID,
      invoice_close_start_date: formattedStartDate,
      invoice_close_end_date: formattedEndDate,
    },
    {
      isWithTotal: params?.isWithTotal,
      disabled: !params.employerId,
      employerId: params.employerId,
      employerIds: params.employerIds,
    }
  );

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.employerId, params?.invoiceID, params.startDate, params.endDate]);

  const [fromDate, toDate] = useMemo(() => {
    const response = rawData?.data;
    const from = response?.date_from
      ? dayjs(response.date_from, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
      : null;
    const to = response?.date_to
      ? dayjs(response.date_to, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
      : null;
    return [from, to];
  }, [rawData?.data]);
  const isEmpty = useMemo(() => {
    return !toDate && !fromDate;
  }, [fromDate, toDate]);
  const title = useMemo(() => {
    if (isEmpty) {
      return null;
    }
    if (fromDate || toDate) {
      return `${fromDate || EMPTY_FIELD_VALUE} — ${toDate || EMPTY_FIELD_VALUE}`;
    }
    return EMPTY_FIELD_VALUE;
  }, [fromDate, isEmpty, toDate]);

  return {
    lastTimeUpdateDate,
    planOptions,
    data,
    isLoading,
    isLoadingMore,
    isSuccess,
    title,
    isEmptyData,
    isHasNextPage,
    handleLoadMore,
  };
};

export default useReplenishmentInvoiceReportSummaryQuery;
